<template>
	<div v-if="isVisible" class="px-4 formField">
		<v-menu
			:close-on-click="false"
			:close-on-content-click="false"
			:open-on-click="false"
			offset-y
			v-model="menuStatus"
		>
			<template v-slot:activator="{ on, attrs }">
				<v-text-field
					@keyup="uppercase"
					:class="getUppercaseClass"
					:disabled="formField.Readonly"
					v-debounce:500ms="onSearchChanged"
					:rules="validationRules"
					:error-messages="validationError"
					:tabindex="tabIndex"
					clearable
					v-bind="attrs"
					v-on="on"
					:label="formField.Label"
					v-model="selectedValue"
					hide-details="auto"
					@click:clear="onClear"
					@focus="onFocus"
					@blur="onBlur"
				>
					<template v-slot:append>
						<v-icon v-if="menuStatus">mdi-menu-up</v-icon>
						<v-icon v-else class="rotate">mdi-menu-up</v-icon>
					</template>
					<template
						v-slot:prepend
						v-if="visibleBubble || visibleHelp"
					>
						<div v-if="visibleBubble">
							<v-tooltip
								top
								nudge-top="20"
								:open-on-click="true"
								:open-on-focus="true"
								:open-on-hover="true"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-icon
										color="primary"
										v-bind="attrs"
										v-on="on"
										:tabindex="tabIndex"
										>mdi-information-outline</v-icon
									>
								</template>
								{{ bubbleText }}
							</v-tooltip>
						</div>
						<div v-if="visibleHelp">
							<v-tooltip
								top
								nudge-top="20"
								:open-on-click="true"
								:open-on-focus="true"
								:open-on-hover="true"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-icon
										color="primary"
										v-bind="attrs"
										v-on="on"
										:tabindex="tabIndex"
										>mdi-help-circle-outline</v-icon
									>
								</template>
								{{ helpText }}
							</v-tooltip>
						</div>
					</template>
					<template v-slot:append-outer v-if="canRetainValue">
						<v-icon v-if="!hasRetainedValue" @click="onPinClick"
							>mdi-pin-off-outline</v-icon
						>
						<v-icon
							v-else
							color="primary"
							class="rotate45"
							@click="onPinClick"
							>mdi-pin-outline</v-icon
						>
					</template>
				</v-text-field>
			</template>
			<div v-if="menuStatus" style="background-color: white">
				<v-overlay absolute="absolute" :value="loading" :dark="false">
					<v-card max-width="200px">
						<v-card-text class="d-flex justify-center">
							<v-progress-circular
								:size="25"
								indeterminate
								color="primary"
							/>
							<span class="mx-3 text-button">Loading</span>
						</v-card-text>
					</v-card>
				</v-overlay>
				<v-list
					v-if="gridViewData"
					max-height="320"
					style="overflow-y: scroll"
				>
					<v-list-item-group
						v-model="selected"
						active-class="primary white--text"
						class="field-list"
					>
						<v-list-item
							v-for="(item, index) in gridViewData"
							@mousedown.prevent="onSelectItem(item)"
							:key="index"
							:value="item"
						>
							<v-list-item-content>
								<v-list-item-title>
									<span
										v-for="(key, index) in keys"
										:key="index"
									>
										<span>{{ item[key] }}</span>
										<span v-if="index < keys.length - 1">{{
											separator
										}}</span>
									</span>
								</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</v-list-item-group>
				</v-list>
				<v-row no-gutters class="ma-2">
					<v-spacer />
					<v-btn
						dense
						small
						color="primary"
						@mousedown.prevent="onShowMore"
						>More</v-btn
					>
				</v-row>
			</div>
		</v-menu>
		<LinkFieldModal
			v-if="currentGridView && showDialog"
			@close="closeDialog()"
			@rowClicked="onRowClicked"
			:dialog="showDialog"
			:gridView="currentGridView"
			:action="loadAction"
			:inventoryFormId="inventoryForm.Id"
		/>
	</div>
</template>
<script>
import { gridViewTypes } from '../../../../enums/gridViewTypes';
import { filterOperators } from '../../../../enums/filterOperators';
import { mapGetters } from 'vuex';
import {
	InventoryFormGetters,
	InventoryFormFormGetters,
} from '../../../../store/getters';
import { InventoryFormFormActions } from '../../../../store/actions';
import gridViewFieldsUtils from '../../../../utils/gridViewFieldsUtils';
import dataFieldUtils from '../../../../utils/dataFieldUtils';
import LinkFieldModal from '../LinkFieldModal.vue';
import formFieldMixin from '../../../../mixins/inventoryForm/formFieldMixin';

export default {
	name: 'LinkFieldMultiple',
	mixins: [formFieldMixin],
	components: { LinkFieldModal },
	data() {
		return {
			loadAction: InventoryFormFormActions.LOAD_GRID_VIEW_DATA,
			hasFocus: false,
			showDialog: false,
			menuStatus: false,
			selected: null,
			gridViewData: [],
			loading: false,
			advancedFilter: { Operator: 1, Filters: [] },
		};
	},
	computed: {
		...mapGetters({
			inventoryForm: [InventoryFormFormGetters.SELECTED_INVENTORY_FORM],
			defaultGridViews: [InventoryFormGetters.GET_GRID_VIEWS],
			gridViews: [InventoryFormFormGetters.GRID_VIEWS],
		}),

		gridViewField() {
			if (!this.formField) return null;
			return this.formField.GridViewFields[0];
		},

		currentGridViewEntityId() {
			if (!this.gridViewFields) return null;
			if (this.gridViewFields.length < 2) return null;
			let gridViewField = this.gridViewFields[0];
			if (!gridViewField.DataField) return null;
			return gridViewField.DataField.EntityId;
		},

		currentGridView() {
			if (!this.currentGridViewEntityId) return null;
			let entityId = this.currentGridViewEntityId;
			let gv = this.gridViews.find(
				(x) =>
					x.Type === gridViewTypes.InventoryFormFilters &&
					x.EntityId == entityId
			);
			if (gv) {
				return gv;
			} else {
				gv = this.defaultGridViews.find(
					(x) =>
						x.Type === gridViewTypes.Grids && x.EntityId == entityId
				);
				return gv;
			}
		},
		separator() {
			if (!this.formField) return '/';
			return this.formField.Separator || '/';
		},
		relatedFields() {
			let result = [];
			if (!this.currentGridView) return result;
			if (!this.gridViewFields) return result;
			this.gridViewFields.forEach((gv) => {
				let corespondingColumn = this.currentGridView.Columns.find(
					(x) => x.DataFieldId === gv.DataFieldId
				);
				let item = {
					gridViewField: gv,
					recordKey: gridViewFieldsUtils.getFieldName(gv),
					curentGridViewCorrespondingGridViewField:
						corespondingColumn,
					currentGridViewKey:
						gridViewFieldsUtils.getFieldName(corespondingColumn),
				};
				result.push(item);
			});
			return result;
		},
		columnsIds() {
			if (!this.relatedFields) return [];
			return this.relatedFields.map(
				(column) => column.curentGridViewCorrespondingGridViewField.Id
			);
		},
		keys() {
			if (!this.relatedFields) return [];
			return this.relatedFields.map(
				(column) => column.currentGridViewKey
			);
		},

		recordSelected() {
			if (!this.relatedFields) return null;
			if (!this.recordSelectedValue) return null;
			let selectedItem = {};
			this.relatedFields.forEach((x) => {
				selectedItem[x.currentGridViewKey] = this.record[x.recordKey];
			});
			let idKey = dataFieldUtils.getDataFieldName(
				this.gridViewField.LinkField
			);
			selectedItem.Id = this.record[idKey];
			return selectedItem;
		},
	},
	mounted() {
		this.defaultRules.push((v) => {
			if (this.formField.Required) {
				if (!v) return false || 'The value is required';
				else return true;
			} else return true;
		});
		if (this.recordSelectedValue) {
			this.selectedValue = this.recordSelectedValue;
			this.selected = this.recordSelected;
		}
	},
	methods: {
		onRecordChanged() {
			if (
				this.recordSelectedValue &&
				this.selectedValue != this.recordSelectedValue
			) {
				this.selectedValue = this.recordSelectedValue;
				this.selected = this.recordSelected;
				return;
			}

			if (
				this.selectedValue &&
				this.selectedValue != this.recordSelectedValue
			) {
				this.selectedValue = null;
				this.selected = null;
			}
		},
		onHideFormField() {
			(this.selectedValue = null), (this.selected = null);
			this.onSelectItem(null);
		},

		onFocus() {
			this.hasFocus = true;
			this.menuStatus = true;
			if (this.gridViewData.length <= 0) {
				this.onSearchChanged();
			}
		},
		onBlur() {
			this.hasFocus = false;
			if (this.selectedValue && this.selectedValue.length > 0) {
				if (this.selected && this.selected.Id > 0) return;
				let selectedItem = {};
				selectedItem['Id'] = null;
				let searchArray = this.selectedValue.split(this.separator);
				let index = 0;
				this.keys.forEach((key) => {
					if (searchArray.length >= index && searchArray[index]) {
						selectedItem[key] = searchArray[index].trim();
					} else {
						selectedItem[key] = null;
					}
					index++;
				});
					let payload = this.buildPayload();
				this.$store
					.dispatch(
						InventoryFormFormActions.LOAD_GRID_VIEW_DATA,
						payload
					)
					.then((resp) => {
						let data = resp.Data;
						let foundMatch = false;

						// Step 1: match all keys exactly
						for (let item of data) {
							let match = true;
							for (let key of this.keys) {
								if (
									(item[key] || '')
										.trim()
										.toLowerCase() !==
									(selectedItem[key] || '')
										.trim()
										.toLowerCase()
								) {
									match = false;
									break;
								}
							}
							if (match) {
								selectedItem = item;
								foundMatch = true;
								break;
							}
						}

						// Step 2: partial match — user entered keys not in order, or not all keys,
						// order-independent: each entered value must match a unique key in the item
						if (!foundMatch) {
							const enteredValues = searchArray
								.map((v) => v.trim().toLowerCase())
								.filter((v) => v !== '');
							if (enteredValues.length > 0) {
								for (let item of data) {
									const usedKeys = new Set();
									const allMatched = enteredValues.every((val) => {
										const matchedKey = this.keys.find(
											(key) =>
												!usedKeys.has(key) &&
												(item[key] || '')
													.trim()
													.toLowerCase() === val
										);
										if (matchedKey) {
											usedKeys.add(matchedKey);
											return true;
										}
										return false;
									});
									if (allMatched) {
										selectedItem = item;
										foundMatch = true;
										break;
									}
								}
							}
						}

						// Step 3: single value fallback — match selectedValue against any key
						if (!foundMatch && this.formField.ForceChoice) {
							const searchTerm = this.selectedValue.trim().toLowerCase();
							for (let item of data) {
								const anyKeyMatch = this.keys.some(
									(key) =>
										(item[key] || '')
											.trim()
											.toLowerCase() === searchTerm
								);
								if (anyKeyMatch) {
									selectedItem = item;
									foundMatch = true;
									break;
								}
							}
						}

						if (!foundMatch && this.formField.ForceChoice) {
							selectedItem = null;
						}
						if (foundMatch) {
							this.selectedValue =
								this.getDisplayValueFromSelectedRow(
									selectedItem
								)
						}

						this.onSelectItem(selectedItem);
					});				
			}
			this.menuStatus = false;
		},

		closeDialog() {
			this.showDialog = false;
			this.menuStatus = false;
		},
		onClear() {
			this.selectedValue = null;
			this.selected = null;
			this.gridViewData = [];
			this.onSelectItem(null);
			this.menuStatus = true;
			this.onSearchChanged();
		},

		onSearchChanged() {
			if (!this.hasFocus) return;
			this.loading = true;
			this.menuStatus = true;
			this.selected = null;
			let payload = this.buildPayload();
			this.$store
				.dispatch(InventoryFormFormActions.LOAD_GRID_VIEW_DATA, payload)
				.then((resp) => {
					this.loading = false;
					if (resp) {
						this.gridViewData = resp.Data;
					} else {
						this.gridViewData = [];
					}
				});
		},

		onShowMore() {
			this.showDialog = true;
			this.menuStatus = false;
		},

		onSelectItem(selectedRow) {
			this.selectedValue =
				this.getDisplayValueFromSelectedRow(selectedRow);
			this.selected = selectedRow;
			this.menuStatus = false;
			this.updateRecord();
		},

		onRowClicked(selectedRow) {
			this.onSelectItem(selectedRow);
			this.closeDialog();
		},

		updateRecord() {
			let keys = [];
			this.relatedFields.forEach((f) => {
				let itemValue = null;
				if (this.selected) {
					itemValue = this.selected[f.currentGridViewKey];
				}
				let item = { key: f.recordKey, value: itemValue };
				keys.push(item);
			});
			let idKey = dataFieldUtils.getDataFieldName(
				this.gridViewField.LinkField
			);
			if (this.selected) {
				keys.push({ key: idKey, value: this.selected.Id });
			} else {
				keys.push({ key: idKey, value: null });
			}
			this.skipNextRecordUpdate = true;
			this.$emit('selectedValueChanged', keys);
			this.menuStatus = false;
		},

		onSelectedValueChanged() {
			//does Nothing -> function is called from mixin; For this component we handle selected not selectedValue;selectedValue=search criteria;
			this.gridViewData = [];
		},
		onSetInteraction(setValue) {
			this.selectedValue = setValue;
			this.selected = null;
			this.gridViewData = [];
			this.onSelectItem(null);
			this.menuStatus = true;
			this.onSearchChanged();
		},
		onClearInteraction() {
			this.selectedValue = null;
			this.selected = null;
			this.gridViewData = [];
			this.onSelectItem(null);
			this.menuStatus = true;
			this.onSearchChanged();
		},
		getAdvancedFilter() {
			if (!this.selectedValue) return null;
			let result = { Operator: 2, Filters: [] };
			let searchArray = this.selectedValue.split(this.separator);
			this.relatedFields.forEach((field) =>
				searchArray.forEach((x) => {
					let filter = {
						Value: x.trim(),
						Operator: filterOperators.Contains,
						FieldId:
							field.curentGridViewCorrespondingGridViewField.Id,
					};
					result.Filters.push(filter);
				})
			);
			return result;
		},

		getDisplayValueFromSelectedRow(selectedRow) {
			if (!selectedRow) return null;
			let values = [];
			this.keys.forEach((x) => {
				if (selectedRow[x]) {
					values.push(selectedRow[x]);
				} else {
					values.push('');
				}
			});
			return values.join(this.separator);
		},
		getDisplayValueFromRecord() {
			let values = [];
			this.recordKeys.forEach((x) => {
				if (this.record[x]) {
					values.push(this.record[x]);
				} else {
					values.push('');
				}
			});
			return values.join(this.separator);
		},
		buildPayload() {
			let payload = {
				ViewId: this.currentGridView.Id,
				QuickFilter: null,
				Page: 1,
				RowsPerPage: 10,
				Columns: this.columnsIds,
				IncludeCount: true,
				InventoryFormId: this.inventoryForm.Id,
			};

			let advancedFilter = this.getAdvancedFilter();
			if (advancedFilter) {
				payload.AdvancedFilter = advancedFilter;
			}
			return payload;
		},
	},
};
</script>
<style lang="scss">
.v-menu__content {
	background-color: white;
}
</style>
